// Ad-copy bridge pages for every degree level + program combination, served
// at /explore/{degree}-{program} (e.g. /explore/bachelors-accounting).
//
// There are ~480 of these, so they are GENERATED from the same option lists
// the forms use (data/filterOptions.ts) rather than hand-authored. A light
// index of every combination is built once for the directory and search; the
// full page content is composed on demand when a slug is requested.
//
// Clicking the CTA sends this page's degreeInterest and mProgram values to
// the Media-Matchers Clicks Search, so the visitor arrives already matched to
// the degree level and program the ad promised.
import {
  degreeLevelOptions,
  areaStudyOptions,
  programOptions,
} from './filterOptions'
import { areaOfStudyData } from './areaOfStudyData'
// Type-only import keeps this module free of a runtime circular dependency
// (exploreBridges imports getProgramBridge from here).
import type { ExploreBridge } from './exploreBridges'

// Publisher group used when the incoming URL carries no publisher params.
const DEFAULT_PUB_GROUP_ID = 'PCGP3406855604'

// Hero imagery per area of study. One photograph of a real person working in
// the field, shared by every program in that area. Every frame is composed
// wide with the subject in the LEFT third, so the hero's right-to-left fade
// lands on open background instead of across a face.
export const AREA_HERO: Record<number, { image: string; alt: string }> = {
  1: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/edbb9216-6529-4b30-ad0d-eb89ab75c647.jpg',
    alt: 'Woman in a light blazer holding a notebook in a bright open-plan office',
  },
  2: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/ffebd712-41ac-48f8-8dbf-e09bcd290a23.jpg',
    alt: 'Man in a dark suit standing in a sunlit courthouse corridor',
  },
  3: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/52c50f4e-a90b-4c69-8e21-bfde3b279bd3.jpg',
    alt: 'Counselor sitting in an armchair in a softly lit therapy office',
  },
  4: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d72db642-27fd-44bf-aa17-678fd13f40bf.jpg',
    alt: 'Man working on a laptop at a desk in a bright home workspace',
  },
  5: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0a87e850-392a-4ed6-9e29-68f88c35242b.jpg',
    alt: 'Medical assistant in scrubs smiling in a bright clinic hallway',
  },
  6: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/c0d5a87b-3242-48d4-99b7-7520811a1483.jpg',
    alt: 'Teacher standing beside a table of students in a sunlit classroom',
  },
  7: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e94eb698-efbe-4418-a4b3-d61c7fe522b1.jpg',
    alt: 'Tradesperson in safety glasses standing in a bright workshop',
  },
  8: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e49d87b1-9257-4915-ae63-aa68b05ad196.jpg',
    alt: 'Student holding a book in a sunlit university library',
  },
  9: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/f489ffed-dc94-4c03-ad87-b47234e1133d.jpg',
    alt: 'Adult student with a backpack on a sunlit campus walkway',
  },
  10: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e15b1ede-3f46-4188-a93c-d2bea17ce226.jpg',
    alt: 'Nurse in scrubs with a stethoscope in a bright hospital corridor',
  },
  11: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/46947523-381b-47b9-8d0d-babb64bca1b2.jpg',
    alt: 'Videographer holding a camera in a daylit studio space',
  },
  12: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/0e66de9b-6177-4fdf-8e27-4c8a048f1a14.jpg',
    alt: 'Designer holding a tablet and stylus beside a desk in a bright studio',
  },
  13: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/f553d12e-99d7-49cb-9d84-e6953b15a9c8.jpg',
    alt: 'Man holding a book beside a tall window in a quiet study',
  },
}

// Per-program hero overrides, for programs whose subject has little to do
// with the rest of their area. Cyber Security sits under Criminal Justice &
// Legal Services in the partner catalog, so the area's courthouse photograph
// reads as the wrong field entirely.
export const PROGRAM_HERO: Record<number, { image: string; alt: string }> = {
  205: {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/6081882c-c390-4110-92b3-4da49d0b0ee3.jpg',
    alt: 'Cybersecurity professional standing beside a desk with network dashboards in a bright daylit office',
  },
}

// How each degree level is framed in the ad copy: what it is, how long it
// takes, and who it suits.
export const DEGREE_COPY: Record<
  number,
  { slug: string; short: string; long: string; pitch: string; timing: string }
> = {
  1: {
    slug: 'certificate',
    short: 'Certificate',
    long: 'certificate',
    pitch:
      'A certificate is the fastest way in. Coursework stays tightly focused on the skills employers screen for, so you spend your time on what the job actually asks of you.',
    timing: 'Many students finish in under a year',
  },
  2: {
    slug: 'associates',
    short: "Associate's",
    long: "associate degree",
    pitch:
      'An associate degree gives you a credential that stands on its own in the job market, and credits that can transfer toward a bachelor’s later if you decide to keep going.',
    timing: 'Typically about two years part or full time',
  },
  3: {
    slug: 'bachelors',
    short: "Bachelor's",
    long: "bachelor's degree",
    pitch:
      'A bachelor’s degree is the credential most employers list on postings in this field, and the one that tends to open the widest range of roles and pay bands.',
    timing: 'Typically four years, fewer with transfer credit',
  },
  4: {
    slug: 'masters',
    short: "Master's",
    long: "master's degree",
    pitch:
      'A master’s degree is built for people already working in the field who want to move into senior, specialized, or leadership roles.',
    timing: 'Typically one to two years',
  },
}

export interface ProgramBridgeEntry {
  slug: string
  degreeInterest: number
  degreeLabel: string
  mProgram: number
  programLabel: string
  areaId: number
  areaLabel: string
}

// The catalog's "×00" entries are the catch-all option for their area and are
// labeled for a dropdown ("General Business", "All Nursing Programs
// (General)"). As a page headline that reads as a hedge, so the word is
// stripped and the area itself becomes the subject.
const GENERAL_LABEL_OVERRIDES: Record<number, string> = {
  // "General/Undecided" has nothing left once the word is removed.
  900: 'Online Degrees',
}
function displayProgramLabel(value: number, label: string): string {
  if (value % 100 !== 0) return label
  if (GENERAL_LABEL_OVERRIDES[value]) return GENERAL_LABEL_OVERRIDES[value]
  return label
    .replace(/^general\s+/i, '')
    .replace(/^all\s+/i, '')
    .replace(/\s*\(general\)\s*$/i, '')
    .replace(/\s+programs$/i, '')
    .trim()
}

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Built once at module load: one entry per degree level × program pairing.
// Program slugs are made unique across areas so two areas offering the same
// program name (e.g. Cyber Security) don't collide on one URL.
function buildIndex(): ProgramBridgeEntry[] {
  const programSlugCounts = new Map<string, number>()
  const programSlugs = new Map<number, string>()
  areaStudyOptions.forEach((area) => {
    ;(programOptions[area.value] || []).forEach((program) => {
      const base = slugify(displayProgramLabel(program.value, program.label))
      const seen = programSlugCounts.get(base) || 0
      programSlugCounts.set(base, seen + 1)
      programSlugs.set(
        program.value,
        seen === 0 ? base : `${base}-${slugify(area.label)}`,
      )
    })
  })
  const entries: ProgramBridgeEntry[] = []
  degreeLevelOptions.forEach((degree) => {
    areaStudyOptions.forEach((area) => {
      ;(programOptions[area.value] || []).forEach((program) => {
        entries.push({
          slug: `${DEGREE_COPY[degree.value].slug}-${programSlugs.get(program.value)}`,
          degreeInterest: degree.value,
          degreeLabel: degree.label,
          mProgram: program.value,
          programLabel: displayProgramLabel(program.value, program.label),
          areaId: area.value,
          areaLabel: area.label,
        })
      })
    })
  })
  return entries
}

export const programBridgeIndex: ProgramBridgeEntry[] = buildIndex()

const bySlug = new Map(programBridgeIndex.map((entry) => [entry.slug, entry]))

// Numeric shorthand: /explore/{degreeInterest}-{mProgram}, e.g. /explore/3-101
// resolves to /explore/bachelors-accounting. Lets a traffic source build the
// URL straight from the codes it already sends.
const byCodes = new Map(
  programBridgeIndex.map((entry) => [
    `${entry.degreeInterest}-${entry.mProgram}`,
    entry,
  ]),
)

// Returns the named slug for a numeric shorthand path, or undefined when the
// slug isn't numeric shorthand or the combination doesn't exist.
export function resolveProgramCodeSlug(
  slug: string | undefined,
): string | undefined {
  if (!slug || !/^\d+-\d+$/.test(slug)) return undefined
  return byCodes.get(slug)?.slug
}

// Composes the full page content for one degree + program combination.
function composeBridge(entry: ProgramBridgeEntry): ExploreBridge {
  const degree = DEGREE_COPY[entry.degreeInterest]
  const area = areaOfStudyData[entry.areaId]
  const hero =
    PROGRAM_HERO[entry.mProgram] || AREA_HERO[entry.areaId] || AREA_HERO[9]
  const program = entry.programLabel
  const paragraphs = [
    `${degree.pitch} ${degree.timing}, and online programs let you keep working while you study.`,
  ]
  if (area?.careerOutlook) paragraphs.push(area.careerOutlook)
  if (area?.whatToLookFor) paragraphs.push(area.whatToLookFor)
  const jobTitles = area?.jobTitles?.slice(0, 3).join(', ')
  // The same program at the other degree levels — the most common next
  // question after "is this the right subject?" is "which level do I need?"
  const otherLevels = programBridgeIndex
    .filter(
      (other) =>
        other.mProgram === entry.mProgram &&
        other.degreeInterest !== entry.degreeInterest,
    )
    .map((other) => ({
      label: `${other.degreeLabel} in ${other.programLabel}`,
      to: `/explore/${other.slug}`,
    }))
  // Sibling programs in the same area at this degree level, for visitors who
  // know the field but haven't settled on a specialization.
  const siblings = programBridgeIndex
    .filter(
      (other) =>
        other.areaId === entry.areaId &&
        other.degreeInterest === entry.degreeInterest &&
        other.mProgram !== entry.mProgram,
    )
    .slice(0, 8)
    .map((other) => ({
      label: other.programLabel,
      to: `/explore/${other.slug}`,
    }))
  const relatedGroups = [
    otherLevels.length && {
      heading: `Other degree levels in ${program}`,
      items: otherLevels,
    },
    siblings.length && {
      heading: `More ${entry.areaLabel} programs`,
      items: siblings,
    },
  ].filter(Boolean) as { heading: string; items: { label: string; to: string }[] }[]
  return {
    slug: entry.slug,
    brand: 'FindOnlineDegrees',
    clicksSearch: {
      defaultPubGroupID: DEFAULT_PUB_GROUP_ID,
      defaultDegreeInterest: String(entry.degreeInterest),
      defaultAreaStudy: String(entry.areaId),
      defaultMProgram: String(entry.mProgram),
    },
    metaTitle: `Online ${degree.short} in ${program} | FindOnlineDegrees`,
    metaDescription: `Compare accredited online ${degree.long} programs in ${program} and get matched with schools enrolling now.`,
    heroImage: hero.image,
    heroImageAlt: hero.alt,
    heroHeadline: `Earn Your ${degree.short} in ${program} Online`,
    cardHeadline: `Get Matched With ${program} Programs`,
    cardSubhead: 'Financial aid may be available for those who qualify',
    ctaLabel: 'Get Program Information',
    sectionHeading: `Why an Online ${degree.short} in ${program}?`,
    paragraphs,
    relatedGroups,
    closingLine: jobTitles
      ? `See which schools are enrolling ${program} students — roles graduates pursue include ${jobTitles}.`
      : `See which schools are currently enrolling ${program} students.`,
    disclosure:
      'FindOnlineDegrees is an advertising-supported site. We may receive compensation when you request information from the schools featured here. Program availability varies by school; not every school offers every program at every degree level.',
  }
}

export function getProgramBridge(
  slug: string | undefined,
): ExploreBridge | undefined {
  if (!slug) return undefined
  const entry = bySlug.get(slug.toLowerCase())
  return entry ? composeBridge(entry) : undefined
}
